import React from "react";
import {
  Box,
  Typography,
  Divider,
  Skeleton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  CheckCircle,
  Cancel,
  PauseCircle,
  SimCard,
  Warning,
} from "@mui/icons-material";

const ESIMActivityTimeline = ({ activities, loading }) => {
  const { t } = useTranslation();

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case "IN_USE":
        return <CheckCircle />;
      case "SUSPENDED":
        return <PauseCircle />;
      case "CANCELLED":
        return <Cancel />;
      case "GOT_RESOURCE":
        return <SimCard />;
      default:
        return <Warning />;
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "IN_USE":
        return "success.main";
      case "SUSPENDED":
        return "warning.main";
      case "CANCELLED":
        return "error.main";
      case "GOT_RESOURCE":
        return "info.main";
      default:
        return "default";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Box>
        {[1, 2, 3].map((item) => (
          <Box key={item} sx={{ mb: 2, display: "flex" }}>
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{ mr: 2 }}
            />
            <Box width="100%">
              <Skeleton width="60%" height={24} />
              <Skeleton width="40%" height={20} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={200}
      >
        <Typography color="textSecondary">
          {t("dashboard.noActivityData")}
        </Typography>
      </Box>
    );
  }

  return (
    <List sx={{ width: "100%", p: 0 }}>
      {activities.slice(0, 5).map((activity, index) => (
        <React.Fragment key={activity.id || index}>
          <ListItem alignItems="flex-start" sx={{ px: 1 }}>
            <ListItemIcon sx={{ minWidth: 42 }}>
              <Avatar
                sx={{
                  bgcolor: getStatusColor(activity.status),
                  width: 32,
                  height: 32,
                }}
              >
                {getStatusIcon(activity.status)}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="subtitle2">
                  {activity.packageName ||
                    activity.package?.name ||
                    t("common.unknownPackage")}
                </Typography>
              }
              secondary={
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="span"
                  >
                    {formatDate(activity.created || activity.createdAt)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: "block" }}>
                    {t(
                      `common.orderStates.${activity.status?.toLowerCase()}.label`,
                      activity.status
                    )}
                  </Typography>
                </>
              }
            />
          </ListItem>
          {index < activities.length - 1 && <Divider component="li" />}
        </React.Fragment>
      ))}
    </List>
  );
};

export default ESIMActivityTimeline;
